/**
 * Voice agent — direct provider integration (013).
 *
 * Inbound request schemas + inferred input types for the voice-agent direct
 * provider flow. Read-only view types live in `types/voice-agent.ts`. The
 * legacy n8n flow's PATCH schema (PatchVoiceAgentConfigRequest) is in
 * `settings.schema.ts` — this file is additive, not a replacement.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Enum schemas
// ---------------------------------------------------------------------------
/**
 * These two must stay in step with `VoiceProviderKind` / `VoiceDirectProvider`
 * in ../types/voice-agent.ts. They are runtime values, so TypeScript will NOT
 * catch a provider added to the type but not here — the mismatch only shows up
 * as "Request validation failed" when someone picks the new provider. The
 * `satisfies` checks below make that a compile error instead.
 */
export const VoiceProviderKindSchema = z.enum([
    'elevenlabs',
    'vapi',
    'retell',
    'nabrah',
    'voiceinfra',
    'legacy_n8n',
]);
export const VoiceAgentModeSchema = z.enum(['legacy_n8n', 'direct']);
export const VoiceDirectProviderSchema = z.enum([
    'elevenlabs',
    'vapi',
    'retell',
    'nabrah',
    'voiceinfra',
]);
const _voiceEnumsInStep = [true, true, true, true];
void _voiceEnumsInStep;
export const VoiceCallStatusSchema = z.enum([
    'pending',
    'in_progress',
    'completed',
    'failed',
    'cancelled',
    'dispatching',
    'ringing',
    'ended',
    'analyzed',
    'timeout',
]);
// ---------------------------------------------------------------------------
// Settings — mode + active-provider mutators
// ---------------------------------------------------------------------------
/**
 * PATCH /api/v1/settings/voice-agent/mode — flips the top-level mode.
 *
 * Setting mode='direct' without an active provider already configured is
 * still allowed (the UI will block the dispatch button until a provider is
 * picked). Switching mode while a call is in flight is blocked server-side
 * with 409 voice_agent_busy_settings_locked.
 */
export const PatchVoiceAgentModeRequestSchema = z.object({
    mode: VoiceAgentModeSchema,
});
/**
 * PATCH /api/v1/settings/voice-agent/active-provider — flips which provider
 * the dispatch route uses. Stored credentials for the other providers stay
 * in the DB so flipping doesn't wipe configuration.
 */
export const PatchVoiceAgentActiveProviderRequestSchema = z.object({
    activeProvider: VoiceDirectProviderSchema,
});
// ---------------------------------------------------------------------------
// Per-provider config PATCH (discriminated union on `provider`)
// ---------------------------------------------------------------------------
const apiKeyField = z
    .string()
    .trim()
    .min(20, 'API key looks too short')
    .max(512, 'API key looks too long')
    .refine((s) => !/\s/.test(s), 'API key cannot contain whitespace');
const webhookSecretField = z
    .string()
    .trim()
    .min(16, 'Webhook secret looks too short')
    .max(512, 'Webhook secret looks too long');
/** ElevenLabs section — needs Twilio phone-number ID imported into EL. */
export const PatchElevenLabsConfigRequestSchema = z.object({
    provider: z.literal('elevenlabs'),
    /** Send to overwrite; omit to leave the stored key untouched. */
    apiKey: apiKeyField.optional(),
    agentId: z.string().trim().min(1).max(128).optional(),
    phoneNumberId: z.string().trim().min(1).max(128).optional(),
    webhookSecret: webhookSecretField.optional(),
});
/** Vapi section — no documented HMAC; serverToken IS the URL-embedded secret. */
export const PatchVapiConfigRequestSchema = z.object({
    provider: z.literal('vapi'),
    apiKey: apiKeyField.optional(),
    assistantId: z.string().trim().min(1).max(128).optional(),
    phoneNumberId: z.string().trim().min(1).max(128).optional(),
    /** Omit on PATCH to keep the existing token; use the dedicated rotate route to regenerate. */
    serverToken: z.string().trim().min(32).max(256).optional(),
});
/** Retell section — straightforward HMAC, BYO or Retell-purchased number. */
export const PatchRetellConfigRequestSchema = z.object({
    provider: z.literal('retell'),
    apiKey: apiKeyField.optional(),
    agentId: z.string().trim().min(1).max(128).optional(),
    /** E.164 (+1234567890). Provider-side number. */
    fromNumber: z
        .string()
        .trim()
        .regex(/^\+[1-9]\d{6,14}$/, 'Use E.164 format e.g. +14155551234')
        .optional(),
    webhookSecret: webhookSecretField.optional(),
});
/**
 * Nabrah section (nabrah.ai) — Saudi/Khaleeji-Arabic voice AI. Outbound via
 * an E.164 `call_from` provisioned to the Nabrah project (bring-your-own SIP
 * trunk). Webhooks are NOT HMAC-signed; Nabrah attaches a static shared-secret
 * header (configured as a post_call_callback parameter) that we compare
 * constant-time — hence `webhookSecret` rather than an HMAC key.
 */
export const PatchNabrahConfigRequestSchema = z.object({
    provider: z.literal('nabrah'),
    apiKey: apiKeyField.optional(),
    agentId: z.string().trim().min(1).max(128).optional(),
    /** E.164 (+966...). The `call_from` number provisioned to the Nabrah project. */
    fromNumber: z
        .string()
        .trim()
        .regex(/^\+[1-9]\d{6,14}$/, 'Use E.164 format e.g. +966512345678')
        .optional(),
    webhookSecret: webhookSecretField.optional(),
});
/**
 * VoiceInfra section (voiceinfra.ai) — UST-3144. Agents run as extensions on
 * an existing PBX, so outbound needs an E.164 `fromNumber` on that trunk, the
 * same bring-your-own shape as Nabrah.
 *
 * Unlike every other provider, VoiceInfra has no fixed API host: it is
 * region-specific and taken from the workspace's own portal address, so the
 * admin supplies `baseUrl`. Webhooks are HMAC-SHA256 signed with a secret set
 * on the webhook in VoiceInfra.
 */
export const PatchVoiceinfraConfigRequestSchema = z.object({
    provider: z.literal('voiceinfra'),
    apiKey: apiKeyField.optional(),
    agentId: z.string().trim().min(1).max(128).optional(),
    /** E.164. The number the PBX presents on outbound calls. */
    fromNumber: z
        .string()
        .trim()
        .regex(/^\+[1-9]\d{6,14}$/, 'Use E.164 format e.g. +14155551234')
        .optional(),
    /** Region-specific API host, e.g. https://eu.voiceinfra.ai. https only. */
    baseUrl: z
        .string()
        .trim()
        .max(256)
        .refine((s) => /^https:\/\/[^\s/]+/.test(s), 'Must be an https URL, e.g. https://eu.voiceinfra.ai')
        .optional(),
    webhookSecret: webhookSecretField.optional(),
});
export const PatchVoiceAgentDirectConfigRequestSchema = z.discriminatedUnion('provider', [
    PatchElevenLabsConfigRequestSchema,
    PatchVapiConfigRequestSchema,
    PatchRetellConfigRequestSchema,
    PatchNabrahConfigRequestSchema,
    PatchVoiceinfraConfigRequestSchema,
]);
// ---------------------------------------------------------------------------
// Verify credentials live (hits provider's read-only endpoint)
// ---------------------------------------------------------------------------
export const VerifyVoiceProviderRequestSchema = z.object({
    provider: VoiceDirectProviderSchema,
});
// ---------------------------------------------------------------------------
// Dispatch outbound call
// ---------------------------------------------------------------------------
/**
 * POST /api/v1/voice-agent/calls — dispatch an outbound call from a CRM
 * contact — or, since v2.8, a `leadId` (a CrmLead, which resolves to its
 * linked contact for dialing). Exactly one of the two must be supplied.
 */
export const DispatchVoiceCallRequestSchema = z
    .object({
    contactId: z.string().uuid('contactId must be a UUID').optional(),
    leadId: z.string().uuid('leadId must be a UUID').optional(),
})
    .refine((d) => !!d.contactId !== !!d.leadId, {
    message: 'Provide exactly one of contactId or leadId',
});
//# sourceMappingURL=voice-agent.schema.js.map